import { useEffect, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { providerAPI } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { Search, FileText, Heart, Calendar, Clock, Loader2, Eye, Filter, User, Shield, X } from 'lucide-react'

interface SharedRecord {
  id: string
  patientId: string
  patientName: string
  type: 'personal' | 'medical'
  documentType?: string
  condition?: string
  sharedAt: string
  expiresAt?: string
  accessLevel: 'view' | 'full'
  description?: string
  notes?: string
}

type RecordTypeFilter = 'all' | 'personal' | 'medical'
type AccessLevelFilter = 'all' | 'view' | 'full'

export default function ProviderRecordsPage() {
  const { toast } = useToast()
  const [records, setRecords] = useState<SharedRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<RecordTypeFilter>('all')
  const [accessFilter, setAccessFilter] = useState<AccessLevelFilter>('all')
  const [selectedRecord, setSelectedRecord] = useState<SharedRecord | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  useEffect(() => {
    fetchRecords()
  }, [])

  const fetchRecords = async () => {
    try {
      setIsLoading(true)
      const response = await providerAPI.getSharedRecords()
      if (response.success) {
        setRecords(response.data.records || [])
      }
    } catch (error: any) {
      console.error('Failed to fetch records:', error)
      setRecords([])
      toast({
        title: "Could not load records",
        description: "Please try again later.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredRecords = records.filter(record => {
    // Search filter
    const matchesSearch = 
      record.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.documentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.condition?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase())
    
    // Type filter
    const matchesType = typeFilter === 'all' || record.type === typeFilter
    
    // Access level filter
    const matchesAccess = accessFilter === 'all' || record.accessLevel === accessFilter
    
    return matchesSearch && matchesType && matchesAccess
  })

  const handleViewRecord = (record: SharedRecord) => {
    setSelectedRecord(record)
    setIsDetailOpen(true)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setTypeFilter('all')
    setAccessFilter('all')
  }

  const hasActiveFilters = searchTerm || typeFilter !== 'all' || accessFilter !== 'all'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Shared Records</h1>
        <p className="text-gray-600">Health records shared with your institution by patients</p>
      </div>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by patient name, type, or condition..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-3">
          {/* Record Type Filter */}
          <Select value={typeFilter} onValueChange={(value: RecordTypeFilter) => setTypeFilter(value)}>
            <SelectTrigger className="w-[160px]">
              <Filter className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Record Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="personal">Personal</SelectItem>
              <SelectItem value="medical">Medical</SelectItem>
            </SelectContent>
          </Select>

          {/* Access Level Filter */}
          <Select value={accessFilter} onValueChange={(value: AccessLevelFilter) => setAccessFilter(value)}>
            <SelectTrigger className="w-[160px]">
              <Shield className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Access Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Access</SelectItem>
              <SelectItem value="view">View Only</SelectItem>
              <SelectItem value="full">Full Access</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button variant="outline" size="icon" onClick={clearFilters} title="Clear filters">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Results Count */}
      {!isLoading && (
        <div className="text-sm text-gray-500">
          Showing {filteredRecords.length} of {records.length} records
          {hasActiveFilters && ' (filtered)'}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#D64463]" />
        </div>
      ) : filteredRecords.length > 0 ? (
        <div className="space-y-3">
          {filteredRecords.map((record) => (
            <Card key={record.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                      record.type === 'personal' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      {record.type === 'personal' ? (
                        <Heart className="h-5 w-5 text-blue-600" />
                      ) : (
                        <FileText className="h-5 w-5 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {record.type === 'personal' 
                          ? (record.condition || 'Personal Health Log')
                          : (record.documentType || 'Medical Record')
                        }
                      </h3>
                      
                      {/* Patient Name */}
                      <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                        <User className="h-3.5 w-3.5" />
                        <span>Shared by {record.patientName}</span>
                      </div>
                      
                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-4 mt-2">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Calendar className="h-3 w-3" />
                          Shared {new Date(record.sharedAt).toLocaleDateString()}
                        </div>
                        {record.expiresAt && (
                          <div className="flex items-center gap-1 text-xs text-amber-600">
                            <Clock className="h-3 w-3" />
                            Expires {new Date(record.expiresAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      
                      {/* Description preview if available */}
                      {record.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {record.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {record.type === 'personal' ? 'Personal' : 'Medical'}
                    </Badge>
                    <Badge className={record.accessLevel === 'full' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                      {record.accessLevel === 'full' ? 'Full Access' : 'View Only'}
                    </Badge>
                    <Button variant="outline" size="sm" onClick={() => handleViewRecord(record)}>
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {hasActiveFilters ? 'No matching records' : 'No shared records'}
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              {hasActiveFilters 
                ? 'Try adjusting your search or filters to find what you\'re looking for.'
                : 'When patients share their health records with your institution, they will appear here.'
              }
            </p>
            {hasActiveFilters && (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Record Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRecord?.type === 'personal' ? (
                <Heart className="h-5 w-5 text-blue-600" />
              ) : (
                <FileText className="h-5 w-5 text-gray-600" />
              )}
              {selectedRecord?.type === 'personal' 
                ? (selectedRecord?.condition || 'Personal Health Log')
                : (selectedRecord?.documentType || 'Medical Record')
              }
            </DialogTitle>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6">
              {/* Patient Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Patient Information</h4>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{selectedRecord.patientName}</span>
                </div>
              </div>

              {/* Record Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Record Type</h4>
                  <Badge variant="outline">
                    {selectedRecord.type === 'personal' ? 'Personal' : 'Medical'}
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Access Level</h4>
                  <Badge className={selectedRecord.accessLevel === 'full' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                    {selectedRecord.accessLevel === 'full' ? 'Full Access' : 'View Only'}
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Shared Date</h4>
                  <p className="text-sm">{new Date(selectedRecord.sharedAt).toLocaleDateString()}</p>
                </div>
                {selectedRecord.expiresAt && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Expires</h4>
                    <p className="text-sm text-amber-600">{new Date(selectedRecord.expiresAt).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedRecord.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Description</h4>
                  <p className="text-sm text-gray-700">{selectedRecord.description}</p>
                </div>
              )}

              {/* Notes */}
              {selectedRecord.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
                  <p className="text-sm text-gray-700">{selectedRecord.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
